import { ViewerConstants } from "../../../shared/model/viewer-constants";

export type AttachmentStatus = "uploading" | "ready" | "error";

export interface ImageContentMetadata {
  id: string;
  preview_url?: string;
}

export interface DocumentContentMetadata {
  id: string;
  preview_url?: string;
  /** All extra fields from the backend extras object are spread here — no data loss. */
  [key: string]: unknown;
}

export interface UrlSource {
  type: "url";
  value: string;
  mimeType?: string;
}

export interface ImageInputContent {
  type: "image";
  source: UrlSource;
  metadata?: ImageContentMetadata;
}

export interface DocumentInputContent {
  type: "document";
  source: UrlSource;
  metadata?: DocumentContentMetadata;
}

export type UserFileContent = ImageInputContent | DocumentInputContent;

/** Backend response from POST /api/files/upload */
export interface UploadResponse {
  id: string;
  filename: string;
  url: string;
  preview_url?: string;
  content_block: UserFileContent;
}

/** UI state of a pending attachment before the message is sent */
export interface AttachedFile {
  /** Client-generated UUID — stable identifier across status changes */
  localId: string;
  file: File;
  status: AttachmentStatus;
  error?: string;
  /** Populated once status === 'ready' — content block ready to include in UserMessage */
  contentBlock?: UserFileContent;
}

export const ACCEPTED_MIME_TYPES = [
  "image/jpeg",
  "image/png",
  "application/json",
  "application/geo+json",
  "text/plain",
  "text/csv",
] as const;

export type AcceptedMimeType = (typeof ACCEPTED_MIME_TYPES)[number];

/**
 * File extensions accepted in addition to MIME types.
 * Used for formats (WKT, GeoJSON) whose MIME type is unreliable across browsers/OS.
 */
export const ACCEPTED_EXTENSIONS = [".wkt", ".geojson"] as const;

/**
 * Returns true when the file is acceptable by MIME type or, as a fallback,
 * by file extension (covers WKT and GeoJSON files that report an empty MIME type).
 */
export function isAcceptedFile(file: File): boolean {
  if ((ACCEPTED_MIME_TYPES as readonly string[]).includes(file.type)) return true;
  const ext = "." + (file.name.split(".").pop() ?? "").toLowerCase();
  return (ACCEPTED_EXTENSIONS as readonly string[]).includes(ext);
}

export const MAX_FILES = 3;

/**
 * The chat's per-file upload cap, kept in sync with MyData's limit
 * (`ViewerConstants.MAX_FILE_SIZE_UPLOAD`, in MB) and never above the backend's
 * own cap (agents-api file store default: 100 MB). VIEWERUX-4715 point 3.
 */
export const MAX_FILE_SIZE_BYTES = ViewerConstants.MAX_FILE_SIZE_UPLOAD * 1024 * 1024;

/**
 * The reason a file cannot be attached to the chat, or null when it can:
 * an unsupported file type, or a file over `MAX_FILE_SIZE_BYTES`.
 * The message names the file so the UI can show it on the rejected chip.
 */
export function rejectionReason(file: File): string | null {
  if (!isAcceptedFile(file)) {
    return `"${file.name}" is not a supported file type.`;
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return `"${file.name}" is too large. The maximum size is ${ViewerConstants.MAX_FILE_SIZE_UPLOAD} MB.`;
  }
  return null;
}
